import { useDokument, useSammlung, type Lesestand, type Zeile } from "../plattform/lesemodelle";
import { useSitzung } from "../plattform/sitzung";

/**
 * Gemeinsames der Ansichten des Kontexts Schuss: Rahmen einer Ansicht der Drückjagd (Zustandsband, `main`; geliefert aus
 * `App.tsx`, weil die Ansicht der Drückjagd dem Kontext Drückjagd gehört) und die Lesemodelle, aus denen ein Gerät seine
 * Lage in der Drückjagd kennt (lesemodelle.md §3, §4, §6).
 */
export type { Rahmen, RahmenProps } from "../plattform/rahmen";

/** Revier, in dessen Einteilung die Person in dieser Drückjagd mitwirkt (`_kennungen.Revier` in Meine Drückjagden). */
export function useEigenesRevier(drueckjagdId: string): string | null {
  const { uid } = useSitzung();
  const eintrag = useDokument(uid ? `personen/${uid}/meine_drueckjagden/${drueckjagdId}` : null);
  return (eintrag.daten?.["_kennungen"] as Record<string, string> | undefined)?.["Revier"] ?? null;
}

export function useRollen(drueckjagdId: string): string[] {
  const { uid } = useSitzung();
  const eintrag = useDokument(uid ? `personen/${uid}/meine_drueckjagden/${drueckjagdId}` : null);
  return (eintrag.daten?.["Rollen"] as string[] | undefined) ?? [];
}

/** Lesemodell eines Reviers in der Drückjagd (lesemodelle.md §4), Tabellensammlung. */
export function useRevierSammlung(drueckjagdId: string, revier: string | null, teil: string): Zeile[] {
  return useRevierSammlungStand(drueckjagdId, revier, teil).daten;
}

/** wie `useRevierSammlung`, mit Lesestand (z. B. für die Startansicht: erst gelesen, dann entschieden) */
export function useRevierSammlungStand(drueckjagdId: string, revier: string | null, teil: string): Lesestand<Zeile[]> {
  return useSammlung(revier ? `drueckjagden/${drueckjagdId}/reviere/${revier}/${teil}` : null);
}

/** Eigene Schüsse der Person (lesemodelle.md §6 „eigene Meldungen“), alle Drückjagden. */
export function useEigeneMeldungen(): Zeile[] {
  return useEigeneMeldungenStand().daten;
}

export function useEigeneMeldungenStand(): Lesestand<Zeile[]> {
  const { uid } = useSitzung();
  return useSammlung(uid ? `personen/${uid}/eigene_meldungen` : null);
}

/** Zugewiesene Suchen der Person in Meine Aufgaben (lesemodelle.md §3). */
export function useZugewieseneSuchen(drueckjagdId: string): Zeile[] {
  return useZugewieseneSuchenStand(drueckjagdId).daten;
}

export function useZugewieseneSuchenStand(drueckjagdId: string): Lesestand<Zeile[]> {
  const { uid } = useSitzung();
  return useSammlung(uid ? `drueckjagden/${drueckjagdId}/meine_aufgaben/${uid}/zugewiesene_suchen` : null);
}

/** Ansprache als ein Text (TESTSPRACHE §3): Wildart, bei Rot- und Damwild mit Altersklasse. */
export function ansprache(d: Zeile | Record<string, unknown>): string {
  const w = d["Wildart"] as string | null | undefined;
  const a = d["Altersklasse"] as string | null | undefined;
  return [w, a].filter(Boolean).join(" ");
}

/** Symbol einer Wildart bzw. Altersklasse (design/README §8.5). */
export function symbolDer(wildart: string, altersklasse?: string | null): string {
  if (altersklasse === "Hirsch") return wildart === "Damwild" ? "hirsch-dam" : "hirsch-rot";
  if (altersklasse) return altersklasse.toLowerCase();
  return { Raubwild: "raubwild", Rehwild: "rehwild", Damwild: "damwild", Rotwild: "rotwild", Schwarzwild: "schwarzwild" }[wildart] ?? "andere-wildart";
}
